import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Modal from 'LuxKit/Modal/Modal'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import noop from 'lib/function/noop'
import { useChatSearch } from 'tripPlanner/hooks/api/chat'
import { getCompactRecentChatTimestampLabel } from 'tripPlanner/assistant/components/AssistantHistoryListItem'
import type { ChatSearchHit, TripPlannerChat } from 'tripPlanner/api/types'

const MIN_QUERY_LENGTH = 2
const SEARCH_DEBOUNCE_MS = 250
const LOAD_MORE_THRESHOLD_PX = 32

const Results = styled.div`
  max-height: ${rem(420)};
  overflow-y: auto;
`

const ResultButton = styled.button`
  display: flex;
  gap: ${rem(16)};
  align-items: flex-start;
  width: 100%;
  border: 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: transparent;
  padding: ${rem(12)} 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: start;

  &:last-child {
    border-bottom: 0;
  }

  &:hover,
  &:focus-visible {
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
`

const ResultText = styled.div`
  flex: 1;
  min-width: 0;
`

const Metadata = styled(BodyText)`
  flex-shrink: 0;
`

const EmptyState = styled(BodyText)`
  padding: ${rem(32)} ${rem(8)};
  text-align: center;
`

const LoadingState = styled.div`
  min-height: ${rem(96)};
`

const ResultsSectionLabel = styled(BodyText)`
  padding: ${rem(4)} 0;
`

const getSearchTerms = (query: string): Array<string> =>
  Array.from(new Set(
    query
      .split(/[^\p{L}\p{N}]+/u)
      .map(term => term.toLocaleLowerCase())
      .filter(term => term.length >= MIN_QUERY_LENGTH),
  ))

function highlightMatches(text: string, searchTerms: Array<string>): Array<React.ReactNode> {
  if (searchTerms.length === 0) return [text]

  const parts: Array<React.ReactNode> = []
  const normalisedText = text.toLocaleLowerCase()
  const matches = searchTerms.flatMap(term => {
    const termMatches: Array<{ start: number; end: number }> = []
    let matchIndex = normalisedText.indexOf(term)

    while (matchIndex >= 0) {
      termMatches.push({ start: matchIndex, end: matchIndex + term.length })
      matchIndex = normalisedText.indexOf(term, matchIndex + term.length)
    }

    return termMatches
  }).sort((left, right) => left.start - right.start || right.end - left.end)
  let cursor = 0

  for (const match of matches) {
    if (match.start < cursor) continue

    if (match.start > cursor) {
      parts.push(text.slice(cursor, match.start))
    }
    parts.push(<strong key={`${match.start}-${text.slice(match.start, match.end)}`}>{text.slice(match.start, match.end)}</strong>)
    cursor = match.end
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor))
  }

  return parts.length > 0 ? parts : [text]
}

interface Props {
  recentChats?: Array<TripPlannerChat>
  onChatSelect: (chatId: number) => void
  onCloseReady?: (closeSearch: () => void) => void
}

export default function TripPlannerChatSearchModal({ recentChats = [], onChatSelect, onCloseReady }: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const trimmedQuery = query.trim()
  const updateDebouncedQuery = useDebouncedCallback(setDebouncedQuery, SEARCH_DEBOUNCE_MS, [])

  useEffect(() => {
    onCloseReady?.(resolve)
    return () => onCloseReady?.(noop)
  }, [onCloseReady, resolve])

  useEffect(() => {
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      updateDebouncedQuery.cancel()
      setDebouncedQuery('')
      return
    }

    updateDebouncedQuery(trimmedQuery)
    return updateDebouncedQuery.cancel
  }, [trimmedQuery, updateDebouncedQuery])

  const searchQuery = useChatSearch(debouncedQuery, {
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
    staleTime: 30_000,
  })
  const searchTerms = useMemo(() => getSearchTerms(debouncedQuery), [debouncedQuery])

  const searchTitle = intl.formatMessage({
    id: 'vGLLJ_NNF92oG64yif5P',
    defaultMessage: 'Search chats',
    description: 'Title shown at the top of the TripPlanner chat search modal',
  })
  const searchPlaceholder = intl.formatMessage({
    id: 'UQZHv3OGmFyLWmnxKRSd',
    defaultMessage: 'Search chats…',
    description: 'Placeholder text shown in the TripPlanner chat search input',
  })
  const searchPrompt = intl.formatMessage({
    id: 'WUbXMeSBX1XQx8NFh_wz',
    defaultMessage: 'Type to search titles and messages',
    description: 'Prompt shown before entering a TripPlanner chat search query',
  })
  const noResults = intl.formatMessage({
    id: '-ROPTPF-mOaRSJv0eDUk',
    defaultMessage: 'No chats found',
    description: 'Empty result message shown when TripPlanner chat search finds no chats',
  })
  const searchError = intl.formatMessage({
    id: 'jrrXW1sY8LxMa3d59cU4',
    defaultMessage: 'Unable to search chats. Please try again.',
    description: 'Error message shown when the TripPlanner chat search request fails',
  })
  const untitledChat = intl.formatMessage({
    id: 'ZvRWxQReAAXTcjXAUal_',
    defaultMessage: 'Untitled chat',
    description: 'Fallback title for a TripPlanner chat search result without a title',
  })
  const recentChatsLabel = intl.formatMessage({
    id: 'FfKLDW_vwahAKXR6CrT3',
    defaultMessage: 'Recent chats',
    description: 'Section label above recent chats in the TripPlanner chat search modal',
  })

  const results = useMemo(() => searchQuery.data?.pages.flatMap(page => page.result) ?? [], [searchQuery.data])
  const recentResults = useMemo<Array<ChatSearchHit>>(() => recentChats.slice(0, 10).map(chat => ({
    chatId: chat.id,
    title: chat.name ?? '',
    matchType: 'title',
    snippet: chat.latestUserMessage,
    updatedAt: chat.updatedAt,
  })), [recentChats])
  const isWaitingForDebounce = trimmedQuery.length >= MIN_QUERY_LENGTH && debouncedQuery !== trimmedQuery
  const showLoading = isWaitingForDebounce || searchQuery.isLoading || searchQuery.isFetching
  const isShowingRecentChats = trimmedQuery.length < MIN_QUERY_LENGTH && recentResults.length > 0
  const displayedResults = isShowingRecentChats ? recentResults : results

  const handleResultsScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { clientHeight, scrollHeight, scrollTop } = event.currentTarget
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX
    if (isNearBottom && searchQuery.hasNextPage && !searchQuery.isFetchingNextPage) {
      searchQuery.fetchNextPage()
    }
  }, [searchQuery])

  const handleSelect = (hit: ChatSearchHit) => {
    resolve()
    onChatSelect(hit.chatId)
  }

  let content: React.ReactNode
  if (trimmedQuery.length < MIN_QUERY_LENGTH && !isShowingRecentChats) {
    content = <EmptyState variant="medium" colour="light-foreground-alt">{searchPrompt}</EmptyState>
  } else if (displayedResults.length > 0) {
    content = (
      <>
        {isShowingRecentChats && (
          <ResultsSectionLabel variant="small" colour="light-foreground-alt">
            {recentChatsLabel}
          </ResultsSectionLabel>
        )}
        <Results aria-busy={!isShowingRecentChats && showLoading} onScroll={handleResultsScroll}>
          {displayedResults.map(hit => {
            const title = hit.title.trim() || untitledChat
            const resultLabel = [title, hit.snippet].filter(Boolean).join(', ')
            return (
              <ResultButton
                key={hit.chatId}
                type="button"
                aria-label={resultLabel}
                onClick={() => handleSelect(hit)}
              >
                <ResultText>
                  <BodyText variant="medium">
                    {isShowingRecentChats ? title : highlightMatches(title, searchTerms)}
                  </BodyText>
                  {!!hit.snippet && (
                    <BodyText variant="small" colour="light-foreground-alt" lineClamp={1}>
                      {isShowingRecentChats ? hit.snippet : highlightMatches(hit.snippet, searchTerms)}
                    </BodyText>
                  )}
                </ResultText>
                <Metadata variant="small" colour="light-foreground-alt">
                  {getCompactRecentChatTimestampLabel(hit.updatedAt, intl)}
                </Metadata>
              </ResultButton>
            )
          })}
          {!isShowingRecentChats && searchQuery.isFetchingNextPage && (
            <LoadingState>
              <LoadingIndicator inline backdrop="none" height={48} />
            </LoadingState>
          )}
        </Results>
      </>
    )
  } else if (showLoading) {
    content = (
      <LoadingState>
        <LoadingIndicator inline backdrop="none" height={96} />
      </LoadingState>
    )
  } else if (searchQuery.isError) {
    content = <EmptyState variant="medium" colour="light-foreground-alt">{searchError}</EmptyState>
  } else if (results.length === 0) {
    content = <EmptyState variant="medium" colour="light-foreground-alt">{noResults}</EmptyState>
  }

  return (
    <Modal
      title={searchTitle}
      size="S"
      height="auto"
      headerExtension={(
        <TextInput
          value={query}
          onChange={event => setQuery(event.currentTarget.value)}
          placeholder={searchPlaceholder}
          aria-label={searchTitle}
          startIcon={<LineSearchIcon />}
          autoFocus
          noValidationSpacing
        />
      )}
    >
      {content}
    </Modal>
  )
}
